import React from 'react';
import { Link } from 'react-router-dom';
import ArticleLayout from '../components/ArticleLayout';
import AssistedApplicationWizard from '../components/AssistedApplicationWizard';
import { getEvidenceById } from '../lib/evidence';
import { MOLLOY_STANDISH_1997 } from '../lib/applicationEvidence';
import { FANOUT_PAGES } from '../lib/site';

const page = FANOUT_PAGES.find((p) => p.path === '/como-aplicar');
const molloy = getEvidenceById('molloy-1991-standardized-mmse');
const guide = MOLLOY_STANDISH_1997;

const ANSWER =
  'Aplicar o MEEM de forma comparável exige usar uma versão definida, preservar sua forma de administração e pontuação, reduzir barreiras corrigíveis antes do teste e documentar condições que possam afetar o desempenho. Este guia organiza a preparação, a aplicação por domínios e o registro do resultado sem reproduzir itens, palavras-alvo ou instruções literais do instrumento licenciado.';

const REFERENCES = [molloy.citation, guide.citation];

const RELATED = [
  {
    to: '/erros-de-aplicacao',
    label: 'Erros comuns na aplicação do Mini Mental',
    note: 'como desvios de método reduzem validade e comparabilidade.'
  },
  {
    to: '/adaptacoes-sensoriais',
    label: 'Déficits sensoriais e motores',
    note: 'como corrigir barreiras sem confundir acomodação com modificação do instrumento.'
  },
  {
    to: '/formulario-e-direitos-de-uso',
    label: 'Formulário e direitos de uso',
    note: 'por que este site não reproduz o roteiro completo do MMSE.'
  },
  {
    to: '/interpretacao-dos-resultados',
    label: 'Como interpretar o resultado',
    note: 'o que fazer com o escore depois de verificar a qualidade da aplicação.'
  },
  {
    to: '/pontos-de-corte',
    label: 'Pontos de corte e referências',
    note: 'como escolher a referência depois de obter um resultado interpretável.'
  },
  {
    to: '/acompanhamento-e-queda-de-pontos',
    label: 'Mudança de pontuação no acompanhamento',
    note: 'como comparar aplicações ao longo do tempo.'
  }
];

export default function ComoAplicar() {
  return (
    <ArticleLayout page={page} answer={ANSWER} related={RELATED} references={REFERENCES}>
      <div aria-label="Aplicar Mini Mental com ajuda">
        <AssistedApplicationWizard />
      </div>

      <section aria-labelledby="atalhos-aplicacao">
        <h2 id="atalhos-aplicacao" className="text-2xl mb-5">Escolha o próximo passo</h2>
        <div className="grid gap-3 sm:grid-cols-2 text-sm">
          <Link to="/" className="rounded-xl border border-[var(--border)] p-4 text-[var(--primary)] hover:underline">
            Calcular a pontuação obtida
          </Link>
          <Link to="/interpretacao-dos-resultados" className="rounded-xl border border-[var(--border)] p-4 text-[var(--primary)] hover:underline">
            Interpretar o resultado
          </Link>
          <Link to="/escolaridade" className="rounded-xl border border-[var(--border)] p-4 text-[var(--primary)] hover:underline">
            Mini Mental e escolaridade
          </Link>
          <Link to="/erros-de-aplicacao" className="rounded-xl border border-[var(--border)] p-4 text-[var(--primary)] hover:underline">
            Erros de aplicação
          </Link>
          <Link to="/quem-pode-aplicar" className="rounded-xl border border-[var(--border)] p-4 text-[var(--primary)] hover:underline">
            Quem pode aplicar
          </Link>
        </div>
      </section>

      <section aria-labelledby="preparacao">
        <h2 id="preparacao" className="text-2xl mb-5">Preparação: antes de aplicar o Mini Mental</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Antes de começar, defina a finalidade da avaliação e identifique qual versão do instrumento será usada. Registre escolaridade, idioma e capacidade de compreensão, além de limitações visuais, auditivas ou motoras que possam interferir no acesso às tarefas.
        </p>
        <p className="text-[var(--text-muted)]">
          Dor importante, sonolência, confusão aguda ou outras condições clínicas transitórias também são interferentes relevantes. Elas não mudam automaticamente a forma de pontuar, mas podem limitar o significado clínico do resultado.
        </p>
      </section>

      <section aria-labelledby="ambiente">
        <h2 id="ambiente" className="text-2xl mb-5">Ambiente e comunicação</h2>
        <p className="text-[var(--text-muted)]">
          Priorize comunicação clara, iluminação suficiente, pouco ruído e interrupções mínimas. Se a pessoa usa habitualmente óculos ou aparelho auditivo, corrija a barreira antes do teste sempre que possível. Isso é diferente de alterar o conteúdo de uma tarefa. Veja a discussão em{' '}
          <Link to="/adaptacoes-sensoriais" className=" text-[var(--primary)] hover:underline">
            déficits sensoriais e motores no Mini Mental
          </Link>.
        </p>
      </section>

      <section aria-labelledby="padronizacao">
        <h2 id="padronizacao" className="text-2xl mb-5">Padronização: use uma versão e um método definidos</h2>
        <p className="text-[var(--text-muted)] mb-4">
          A padronização importa porque diferenças na administração e na pontuação introduzem ruído de medida. No estudo de Molloy et al., a versão padronizada reduziu a variância intravaliador em {molloy.results.intraraterVarianceReductionPct}% e a variância interavaliador em {molloy.results.interraterVarianceReductionPct}%, com aumento da correlação intraclasse de {molloy.results.iccBefore} para {molloy.results.iccAfter}.
        </p>
        <p className="text-[var(--text-muted)]">
          O guia metodológico posterior de Molloy e Standish também destaca que instruções pouco específicas permitem variação entre aplicadores e reduzem a confiabilidade. Portanto, não misture variantes, não improvise critérios e não suponha que formas alternativas sejam numericamente equivalentes.
        </p>
      </section>

      <section aria-labelledby="dominios-aplicacao">
        <h2 id="dominios-aplicacao" className="text-2xl mb-5">Aplicação por domínios: o que organizar</h2>
        <p className="text-[var(--text-muted)] mb-6">
          O modo assistido acompanha a sequência geral e o registro de pontos. Quando um domínio depende de estímulos ou comandos específicos, aplique a tarefa correspondente conforme a versão validada ou licenciada utilizada em sua prática.
        </p>

        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-bold text-[var(--primary)] mb-2">Orientação</h3>
            <p className="text-[var(--text-muted)]">Conduza os componentes de orientação previstos na versão adotada sem acrescentar pistas ou reformulações que alterem a dificuldade. Registre os pontos obtidos conforme os critérios dessa versão.</p>
          </div>
          <div>
            <h3 className="text-lg font-bold text-[var(--primary)] mb-2">Registro</h3>
            <p className="text-[var(--text-muted)]">Use apenas os estímulos definidos pela versão do instrumento utilizada. Preserve a forma de apresentação e registre a pontuação correspondente sem criar palavras substitutas para este site.</p>
          </div>
          <div>
            <h3 className="text-lg font-bold text-[var(--primary)] mb-2">Atenção e cálculo</h3>
            <p className="text-[var(--text-muted)]">Aplique a tarefa de atenção prevista na versão em uso. Não troque o método durante a avaliação nem crie uma atividade alternativa para conservar a mesma pontuação.</p>
          </div>
          <div>
            <h3 className="text-lg font-bold text-[var(--primary)] mb-2">Evocação</h3>
            <p className="text-[var(--text-muted)]">Retome a tarefa de evocação conforme o instrumento utilizado e preserve as regras previstas para resposta espontânea e correção.</p>
          </div>
          <div>
            <h3 className="text-lg font-bold text-[var(--primary)] mb-2">Linguagem</h3>
            <p className="text-[var(--text-muted)]">Siga as tarefas, materiais e critérios da versão adotada. Barreiras de idioma, audição, alfabetização ou compreensão devem ser registradas como contexto, não compensadas com uma regra própria de pontuação.</p>
          </div>
          <div>
            <h3 className="text-lg font-bold text-[var(--primary)] mb-2">Capacidade visuoconstrutiva</h3>
            <p className="text-[var(--text-muted)]">Utilize o material correspondente à versão adotada em sua prática. O MiniMental.com.br não apresenta uma figura alternativa nem propõe um estímulo substituto para gerar a mesma pontuação.</p>
          </div>
        </div>
      </section>

      <section aria-labelledby="escolaridade-contexto">
        <h2 id="escolaridade-contexto" className="text-2xl mb-5">Escolaridade: aplicação e interpretação não são a mesma coisa</h2>
        <p className="text-[var(--text-muted)]">
          A escolaridade é especialmente importante para contextualizar o desempenho e escolher a referência interpretativa apropriada. Ela não autoriza modificar espontaneamente as tarefas durante a aplicação. Para aprofundar essa etapa, consulte{' '}
          <Link to="/escolaridade" className=" text-[var(--primary)] hover:underline">Mini Mental e escolaridade</Link>.
        </p>
      </section>

      <section aria-labelledby="erros-comuns">
        <h2 id="erros-comuns" className="text-2xl mb-5">Erros comuns durante a aplicação</h2>
        <ul className="list-disc pl-5 space-y-3 text-[var(--text-muted)]">
          <li>misturar versões ou critérios de pontuação;</li>
          <li>dar pistas ou reformular tarefas de modo não previsto;</li>
          <li>substituir uma tarefa por outra criada pelo aplicador;</li>
          <li>ignorar barreiras sensoriais, linguísticas ou clínicas relevantes;</li>
          <li>comparar resultados de aplicações pouco equivalentes como se fossem diretamente intercambiáveis.</li>
        </ul>
        <p className="mt-4 text-[var(--text-muted)]">
          Veja a discussão completa em{' '}
          <Link to="/erros-de-aplicacao" className=" text-[var(--primary)] hover:underline">erros comuns na aplicação do Mini Mental</Link>.
        </p>
      </section>

      <section aria-labelledby="registro-contexto">
        <h2 id="registro-contexto" className="text-2xl mb-5">O que registrar além da pontuação?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Para preservar comparabilidade, registre a data, a versão utilizada, condições da aplicação, barreiras sensoriais ou motoras relevantes, desvios ocorridos e o escore obtido.
        </p>
        <p className="text-[var(--text-muted)]">
          O wizard desta página registra apenas os pontos em memória enquanto a página permanece aberta. Não há envio de pontuação para backend, banco de dados ou analytics.
        </p>
      </section>

      <section aria-labelledby="interpretacao-contextual">
        <h2 id="interpretacao-contextual" className="text-2xl mb-5">Interpretação contextual depois da aplicação</h2>
        <p className="text-[var(--text-muted)]">
          Antes de perguntar se o escore está “normal” ou “baixo”, confirme se a aplicação foi suficientemente comparável à referência que pretende usar. Só então considere escolaridade, população do estudo, queixa cognitiva, funcionalidade e evolução. Continue em{' '}
          <Link to="/interpretacao-dos-resultados" className=" text-[var(--primary)] hover:underline">como interpretar o resultado do Mini Mental</Link>{' '}
          e consulte também os{' '}
          <Link to="/pontos-de-corte" className=" text-[var(--primary)] hover:underline">pontos de corte e referências brasileiras</Link>.
        </p>
      </section>

      <section aria-labelledby="comparabilidade-longitudinal">
        <h2 id="comparabilidade-longitudinal" className="text-2xl mb-5">Quando a comparabilidade exige cautela?</h2>
        <p className="text-[var(--text-muted)]">
          A cautela aumenta quando houve aplicação modificada, barreira sensorial não resolvida, quadro clínico agudo, versão não identificada ou condições muito diferentes de uma avaliação anterior. Nesses cenários, uma diferença de pontuação não deve ser atribuída automaticamente a mudança cognitiva. Veja{' '}
          <Link to="/acompanhamento-e-queda-de-pontos" className=" text-[var(--primary)] hover:underline">queda de pontos no acompanhamento</Link>.
        </p>
      </section>

      <section aria-labelledby="direitos-aplicacao">
        <h2 id="direitos-aplicacao" className="text-2xl mb-5">Este guia não substitui o instrumento</h2>
        <p className="text-[var(--text-muted)]">
          O site auxilia sequência, administração geral, registro, pontuação e interpretação. Ele não fornece o roteiro completo do MMSE, não reproduz estímulos específicos e não cria uma versão modificada. Para entender os limites de reprodução e uso, consulte{' '}
          <Link to="/formulario-e-direitos-de-uso" className=" text-[var(--primary)] hover:underline">formulário e direitos de uso</Link>.
        </p>
      </section>

      <section aria-labelledby="faq-aplicacao">
        <h2 id="faq-aplicacao" className="text-2xl mb-5">Perguntas frequentes sobre aplicação</h2>
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-bold text-[var(--primary)] mb-2">Como aplicar o Mini Mental?</h3>
            <p className="text-[var(--text-muted)]">Defina a versão que será usada, prepare o ambiente, siga a administração prevista nessa versão, registre a pontuação por domínio e documente interferentes relevantes antes de interpretar o resultado. O modo assistido desta página ajuda a organizar esse fluxo sem reproduzir o formulário.</p>
          </div>
          <div>
            <h3 className="text-lg font-bold text-[var(--primary)] mb-2">Quanto tempo leva a aplicação?</h3>
            <p className="text-[var(--text-muted)]">O tempo pode variar conforme a pessoa avaliada, o contexto e a versão utilizada. A prioridade é preservar a administração prevista no instrumento e registrar condições que possam ter alterado a aplicação, em vez de forçar um tempo único para todos os casos.</p>
          </div>
          <div>
            <h3 className="text-lg font-bold text-[var(--primary)] mb-2">Posso alterar palavras ou tarefas do Mini Mental?</h3>
            <p className="text-[var(--text-muted)]">Alterações podem mudar a padronização e a comparabilidade do resultado. O profissional deve seguir a versão adotada e documentar qualquer desvio inevitável em vez de tratar uma adaptação improvisada como equivalente.</p>
          </div>
          <div>
            <h3 className="text-lg font-bold text-[var(--primary)] mb-2">Como corrigir o Mini Mental?</h3>
            <p className="text-[var(--text-muted)]">A correção deve seguir os critérios da versão utilizada. O MiniMental.com.br pode ajudar a registrar e somar os pontos informados e depois direcionar para a interpretação contextual, mas não cria um critério de correção independente.</p>
          </div>
          <div>
            <h3 className="text-lg font-bold text-[var(--primary)] mb-2">Escolaridade interfere na aplicação?</h3>
            <p className="text-[var(--text-muted)]">A escolaridade é particularmente relevante para interpretação e comparação com referências. Ela não deve ser usada como justificativa para modificar livremente as tarefas durante a aplicação.</p>
          </div>
        </div>
      </section>
    </ArticleLayout>
  );
}
